import React from 'react';
import { Typography, Tooltip, Box, TypographyProps } from '@mui/material';

interface IndianAmountDisplayProps {
  amount: number;
  showSymbol?: boolean;
  showInWords?: boolean;
  showInLakhs?: boolean;
  showInCrores?: boolean;
  decimals?: number;
  color?: 'positive' | 'negative' | 'neutral' | 'auto';
  size?: 'small' | 'medium' | 'large';
  variant?: TypographyProps['variant'];
  bold?: boolean;
  showTooltip?: boolean;
}

// Convert number to Indian format (lakhs, crores)
const formatIndianNumber = (num: number, decimals: number = 2): string => {
  if (num === 0) return '0.00';

  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Indian number system: 1,00,000 (1 lakh), 1,00,00,000 (1 crore)
  const formatter = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const formatted = formatter.format(absNum);
  return isNegative ? `-${formatted}` : formatted;
};

// Convert number to words (Indian format)
const numberToWords = (num: number): string => {
  if (num === 0) return 'Zero';

  const isNegative = num < 0;
  const absNum = Math.abs(num);

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertToWords = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertToWords(n % 100) : '');
    if (n < 100000) return convertToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convertToWords(n % 1000) : '');
    if (n < 10000000) return convertToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convertToWords(n % 100000) : '');
    return convertToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 !== 0 ? ' ' + convertToWords(n % 10000000) : '');
  };

  // Handle decimal part
  const intPart = Math.floor(absNum);
  const decPart = Math.round((absNum - intPart) * 100);

  let result = convertToWords(intPart);
  if (decPart > 0) {
    result += ' and ' + convertToWords(decPart) + ' Paise';
  }

  return (isNegative ? 'Minus ' : '') + result + ' Only';
};

// Format in lakhs
const formatInLakhs = (num: number): string => {
  const lakhs = num / 100000;
  if (Math.abs(lakhs) >= 1) {
    return `${lakhs.toFixed(2)} L`;
  }
  return formatIndianNumber(num);
};

// Format in crores
const formatInCrores = (num: number): string => {
  const crores = num / 10000000;
  if (Math.abs(crores) >= 1) {
    return `${crores.toFixed(2)} Cr`;
  }
  const lakhs = num / 100000;
  if (Math.abs(lakhs) >= 1) {
    return `${lakhs.toFixed(2)} L`;
  }
  return formatIndianNumber(num);
};

const IndianAmountDisplay: React.FC<IndianAmountDisplayProps> = ({
  amount,
  showSymbol = true,
  showInWords = false,
  showInLakhs = false,
  showInCrores = false,
  decimals = 2,
  color = 'auto',
  size = 'medium',
  variant,
  bold = false,
  showTooltip = true,
}) => {
  // Determine color based on amount or explicit setting
  const getColor = (): string => {
    if (color === 'auto') {
      if (amount > 0) return 'success.main';
      if (amount < 0) return 'error.main';
      return 'text.primary';
    }
    switch (color) {
      case 'positive': return 'success.main';
      case 'negative': return 'error.main';
      case 'neutral': return 'text.primary';
      default: return 'text.primary';
    }
  };

  // Determine font size
  const getFontSize = (): string => {
    switch (size) {
      case 'small': return '0.875rem';
      case 'large': return '1.25rem';
      default: return '1rem';
    }
  };

  // Format the display amount
  let displayAmount: string;
  if (showInCrores) {
    displayAmount = formatInCrores(amount);
  } else if (showInLakhs) {
    displayAmount = formatInLakhs(amount);
  } else {
    displayAmount = formatIndianNumber(amount, decimals);
  }

  const fullDisplay = showSymbol ? `₹ ${displayAmount}` : displayAmount;
  const tooltipContent = `₹ ${formatIndianNumber(amount, decimals)}${showInWords ? '\n' + numberToWords(amount) : ''}`;

  const content = (
    <Typography
      component="span"
      variant={variant}
      sx={{
        color: getColor(),
        fontSize: variant ? undefined : getFontSize(),
        fontWeight: bold ? 600 : 400,
        fontFamily: 'monospace',
        whiteSpace: 'nowrap',
      }}
    >
      {fullDisplay}
    </Typography>
  );

  if (showInWords) {
    return (
      <Box>
        {showTooltip ? (
          <Tooltip title={tooltipContent} arrow placement="top">
            {content}
          </Tooltip>
        ) : (
          content
        )}
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          sx={{ fontStyle: 'italic', mt: 0.5 }}
        >
          {numberToWords(amount)}
        </Typography>
      </Box>
    );
  }

  if (showTooltip && (showInLakhs || showInCrores)) {
    return (
      <Tooltip title={`₹ ${formatIndianNumber(amount, decimals)}`} arrow placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

// Export utility functions for use elsewhere
export { formatIndianNumber, numberToWords, formatInLakhs, formatInCrores };

export default IndianAmountDisplay;
